import { Injectable } from '@angular/core';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import _ from 'lodash';
import { tap } from 'rxjs/operators';
import { LanguageType } from 'src/app/samwebDescription/samweb/settings/language-types';
import { ThemeType } from 'src/app/samwebDescription/samweb/settings/theme-type';
import { ShortcutTypeGlobal } from 'src/app/samwebDescription/samweb/shortcuts/shortcutTypeGlobal';
import {
   PrepareLocalStorageSettingsForModification,
   RejectChangedLocalStorageSettings,
   SaveLocalStorageSettings,
} from 'src/app/store/local-storage/local-storage-settings/audio-devices-settings.actions';
import { CloseHotkeyDuplicationDialog } from 'src/app/store/samwin/shortcut-behaviour/shortcut-behaviour.actions';
import { SettingsService } from 'src/generated/swagger';
import { PersistAgentGreetings, SetAgentGreetingsIsInEditing } from './agent-greeting-settings/agent-greeting-settings.actions';
import { AgentGreetingSettingsState } from './agent-greeting-settings/agent-greeting-settings.state';
import { AudioSettingsState } from './audio-settings/audio-settings.state';
import {
   ChangeCurrentTheme,
   RejectChangedSettings,
   SaveSettings,
   SetCurrentHotkeySettings,
   SetTemporaryTheme,
   SettingsChangeApplicationLanguage,
   SettingsChangeBackgroundImage,
   SettingsChangeBrickWallMargin,
   SettingsChangeGlobalBrickScaling,
   SettingsChangeTheme,
   SettingsChangeWarnBeforeClosingTab,
   SettingsChangedByServer,
   SettingsChangedHotkeyCombo,
   ToggleGlobalSettingsDialog,
   UpdateDraftSettings,
} from './settings.actions';

export interface SamwebSettingsDescription {
   applicationLanguage: LanguageType;
   theme: ThemeType;
   backgroundImageUrl: string;
   globalBrickScaling: number;
   hotkeys: { [key: string]: string };
   warnBeforeClosingTab: boolean;
   brickWallMargin: number;
}

export interface SettingsStateModel {
   isOpen: boolean;
   areSettingsInitialized: boolean;
   currentSettings: SamwebSettingsDescription;
   draftSettings: SamwebSettingsDescription;
   temporaryTheme?: ThemeType;
}

@State<SettingsStateModel>({
   name: 'settings',
   defaults: {
      isOpen: false,
      areSettingsInitialized: false,
      draftSettings: null,
      currentSettings: {
         applicationLanguage: LanguageType.ENGLISH,
         theme: ThemeType.DEFAULT,
         backgroundImageUrl: null,
         globalBrickScaling: 100,
         hotkeys: {},
         warnBeforeClosingTab: true,
         brickWallMargin: 5,
      },
   },
   children: [AudioSettingsState, AgentGreetingSettingsState],
})
@Injectable()
export class SettingsState {
   constructor(private settingsService: SettingsService, private store: Store) {}

   @Selector([SettingsState])
   public static getSettings(state: SettingsStateModel): SamwebSettingsDescription {
      return state.draftSettings ? state.draftSettings : state.currentSettings;
   }

   @Selector([SettingsState.getSettings])
   public static getCurrentLanguage(settings: SamwebSettingsDescription): LanguageType {
      return settings.applicationLanguage;
   }

   @Selector()
   public static getFallbackLanguage(): LanguageType {
      return LanguageType.ENGLISH;
   }

   @Selector([SettingsState])
   public static isSettingsDialogOpen(state: SettingsStateModel) {
      return state.isOpen;
   }

   @Selector([SettingsState.getSettings])
   public static applicationTheme(settings: SamwebSettingsDescription) {
      return settings.theme;
   }

   @Selector([SettingsState])
   public static applicationTemporaryTheme(state: SettingsStateModel) {
      return state.temporaryTheme;
   }

   @Selector([SettingsState.applicationTheme, SettingsState.applicationTemporaryTheme])
   public static appliedTheme(applicationTheme: ThemeType, applicationTemporaryTheme: ThemeType) {
      return applicationTemporaryTheme ?? applicationTheme;
   }

   @Selector([SettingsState.getSettings])
   public static getBackgroundImageUrl(settings: SamwebSettingsDescription): string {
      return settings.backgroundImageUrl;
   }

   @Selector([SettingsState.getSettings])
   public static getGlobalBrickScaling(settings: SamwebSettingsDescription) {
      return settings.globalBrickScaling;
   }

   @Selector([SettingsState.getSettings])
   public static getWarnBeforeClosingTab(settings: SamwebSettingsDescription) {
      return settings.warnBeforeClosingTab;
   }

   @Selector([SettingsState.getSettings])
   public static getHotkeySettings(settings: SamwebSettingsDescription) {
      return settings.hotkeys;
   }

   @Selector([SettingsState.getSettings])
   public static getHotkeySettingsRecords(settings: SamwebSettingsDescription): Record<string, string> {
      return settings.hotkeys;
   }

   @Selector([SettingsState.getHotkeySettingsRecords])
   public static getHotkeySettingForHotkeyType(hotkeySettings: Record<string, string>) {
      return (hotkeyTyp: ShortcutTypeGlobal) => {
         return hotkeySettings[hotkeyTyp.value];
      };
   }

   @Selector([SettingsState.getSettings])
   public static getBrickWallMargin(settings: SamwebSettingsDescription): number {
      return settings.brickWallMargin;
   }

   @Selector([SettingsState])
   public static getEffectiveHotkeySettingForHotkeyType(settings: SettingsStateModel) {
      return (hotkeyTyp: ShortcutTypeGlobal) => {
         const combo = settings.currentSettings.hotkeys[hotkeyTyp.value];
         if (_.isNil(combo)) {
            return hotkeyTyp.defaultHotkeyCombo;
         }
         return combo;
      };
   }

   @Action(SettingsChangeApplicationLanguage)
   @ImmutableContext()
   public changeApplicationLanguage(ctx: StateContext<SettingsStateModel>, action: SettingsChangeApplicationLanguage) {
      ctx.setState((state) => {
         state.draftSettings.applicationLanguage = action.applicationLanguage;
         return state;
      });
   }

   @Action(SettingsChangedHotkeyCombo)
   @ImmutableContext()
   public settingsChangedHotkeyCombo(ctx: StateContext<SettingsStateModel>, action: SettingsChangedHotkeyCombo) {
      ctx.setState((state) => {
         state.draftSettings.hotkeys[action.shortcutType.value] = action.combo;
         return state;
      });
      this.store.dispatch(new CloseHotkeyDuplicationDialog(action.shortcutType));
   }

   @Action(SetCurrentHotkeySettings)
   @ImmutableContext()
   public setCurrentHotkeySettings(ctx: StateContext<SettingsStateModel>, action: SetCurrentHotkeySettings) {
      ctx.setState((state) => {
         state.currentSettings.hotkeys = action.shortcuts;
         return state;
      });
   }

   @Action(SettingsChangeTheme)
   @ImmutableContext()
   public settingsChangeTheme(ctx: StateContext<SettingsStateModel>, action: SettingsChangeTheme) {
      ctx.setState((state) => {
         state.draftSettings.theme = action.theme;
         return state;
      });
   }

   @Action(ChangeCurrentTheme)
   @ImmutableContext()
   public changeCurrentTheme(ctx: StateContext<SettingsStateModel>, action: ChangeCurrentTheme) {
      ctx.setState((state) => {
         state.currentSettings.theme = action.theme;
         return state;
      });
   }

   @Action(SetTemporaryTheme)
   public setTemporaryTheme(ctx: StateContext<SettingsStateModel>, action: SetTemporaryTheme) {
      ctx.patchState({
         temporaryTheme: action.theme,
      });
   }

   @Action(UpdateDraftSettings)
   public updateDraftSettings(ctx: StateContext<SettingsStateModel>, action: UpdateDraftSettings) {
      const currentSettings = _.cloneDeep(ctx.getState().currentSettings);
      ctx.patchState({
         draftSettings: currentSettings,
      });
   }

   @Action(ToggleGlobalSettingsDialog)
   public toggleGlobalSettingsDialog(ctx: StateContext<SettingsStateModel>, action: ToggleGlobalSettingsDialog) {
      const newIsOpenValue = !ctx.getState().isOpen;
      ctx.patchState({
         draftSettings: newIsOpenValue ? _.cloneDeep(ctx.getState().currentSettings) : null,
         isOpen: newIsOpenValue,
      });

      // update child setting states
      ctx.dispatch(new SetAgentGreetingsIsInEditing(newIsOpenValue));

      if (newIsOpenValue) {
         return this.store.dispatch(new PrepareLocalStorageSettingsForModification());
      } else {
         return this.store.dispatch(new RejectChangedLocalStorageSettings());
      }
   }

   @Action(RejectChangedSettings)
   public rejectChangedSettings(ctx: StateContext<SettingsStateModel>, action: RejectChangedSettings) {
      const isOpenValue = ctx.getState().isOpen;
      ctx.patchState({
         draftSettings: isOpenValue ? _.cloneDeep(ctx.getState().currentSettings) : null,
         isOpen: false,
      });

      // update child setting states
      ctx.dispatch(new SetAgentGreetingsIsInEditing(false));

      return this.store.dispatch(new RejectChangedLocalStorageSettings());
   }

   @Action(SettingsChangeBackgroundImage)
   @ImmutableContext()
   public changeBackgroundImage(ctx: StateContext<SettingsStateModel>, action: SettingsChangeBackgroundImage) {
      ctx.setState((state) => {
         state.draftSettings.backgroundImageUrl = action.backgroundImageUrl;
         return state;
      });
   }

   @Action(SettingsChangeGlobalBrickScaling)
   @ImmutableContext()
   public changeSettingsChangeGlobalBrickScaling(ctx: StateContext<SettingsStateModel>, action: SettingsChangeGlobalBrickScaling) {
      ctx.setState((state) => {
         state.draftSettings.globalBrickScaling = action.globalBrickScaling;
         return state;
      });
   }

   @Action(SettingsChangeWarnBeforeClosingTab)
   @ImmutableContext()
   public changeWarnBeforeClosingTabSetting(ctx: StateContext<SettingsStateModel>, action: SettingsChangeWarnBeforeClosingTab) {
      ctx.setState((state) => {
         state.draftSettings.warnBeforeClosingTab = action.warnBeforeClosingTab;
         return state;
      });
   }

   @Action(SettingsChangeBrickWallMargin)
   @ImmutableContext()
   public changeBrickWallMargin(ctx: StateContext<SettingsStateModel>, action: SettingsChangeBrickWallMargin) {
      ctx.setState((state) => {
         state.draftSettings.brickWallMargin = action.margin;
         return state;
      });
   }

   @Action(SaveSettings)
   public saveSettings(ctx: StateContext<SettingsStateModel>, action: SettingsChangeGlobalBrickScaling) {
      // update child settings
      ctx.dispatch(new PersistAgentGreetings());

      const settings = ctx.getState().draftSettings;
      return this.settingsService
         .changeUserSettings({
            changedSettings: {
               theme: settings.theme.value,
               backgroundImage: settings.backgroundImageUrl,
               applicationLanguage: settings.applicationLanguage.value,
               globalBrickScaling: settings.globalBrickScaling,
               hotkeyCombos: settings.hotkeys,
               warnBeforeClosingTab: settings.warnBeforeClosingTab,
               brickWallMargin: settings.brickWallMargin,
            },
         })
         .pipe(
            tap(() =>
               ctx.patchState({
                  isOpen: false,
               })
            )
         )
         .pipe(tap(() => this.store.dispatch(new SaveLocalStorageSettings())));
   }

   @Action(SettingsChangedByServer)
   @ImmutableContext()
   public settingsChangedByServer(ctx: StateContext<SettingsStateModel>, action: SettingsChangedByServer) {
      ctx.setState((state: SettingsStateModel) => {
         if (action.userSettings.globalBrickScaling > 0) {
            state.currentSettings.globalBrickScaling = action.userSettings.globalBrickScaling;
         }
         if (!_.isEmpty(action.userSettings.applicationLanguage)) {
            state.currentSettings.applicationLanguage = LanguageType.valueOf(action.userSettings.applicationLanguage);
         }
         if (!_.isEmpty(action.userSettings.theme)) {
            state.currentSettings.theme = ThemeType.valueOf(action.userSettings.theme);
         }
         if (action.userSettings.brickWallMargin >= 0) {
            state.currentSettings.brickWallMargin = action.userSettings.brickWallMargin;
         }
         state.currentSettings.hotkeys = action.userSettings.hotkeyCombos;
         state.currentSettings.backgroundImageUrl = action.userSettings.backgroundImage;
         state.currentSettings.warnBeforeClosingTab = action.userSettings.warnBeforeClosingTab;

         state.draftSettings = state.isOpen ? _.cloneDeep(state.currentSettings) : null;
         state.areSettingsInitialized = true;
         return state;
      });
   }
}
